import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  LineChart,
  Line,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from "recharts";

const DemographicsCharts = ({
  ageDistributionData = [],
  entryExitData = [],
  genderByFieldData = [],
}) => {
  // 🧹 Clean Age Distribution data
  const cleanedAgeData = (ageDistributionData || [])
    .filter((item) => item && item.age && item.age.toLowerCase() !== "total")
    .map((item) => ({
      ...item,
      graduates: Number(item.graduates) || 0,
      femalePercent: Number(item.femalePercent) || 0,
      malePercent: Number(item.malePercent) || 0,
    }));

  // 🧮 Combine Entry/Exit data per field
  const cleanedEntryExitData = Object.values(
    (entryExitData || [])
      .filter((item) => item && item.field)
      .reduce((acc, item) => {
        const key = item.field.trim();
        if (!acc[key]) {
          acc[key] = { fieldOfStudy: key, entries: 0, exits: 0 };
        }
        acc[key].entries += Number(item.entry) || 0;
        acc[key].exits += Number(item.exit) || 0;
        return acc;
      }, {})
  ).sort((a, b) => b.entries - a.entries);

  // 🧹 Clean Gender Balance data
  const cleanedGenderData = (genderByFieldData || [])
    .filter((item) => item && item.studyField)
    .map((item) => ({
      studyField: item.studyField,
      femalePercent: Number(item.femalePercent) || 0,
      malePercent: Number(item.malePercent) || 0,
    }))
    .filter((item) => item.femalePercent !== 0 || item.malePercent !== 0);

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {/* 🟧 Age Distribution & Employment */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 600, display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Age Distribution & Employment
            </Typography>

            {cleanedAgeData.length > 0 ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={cleanedAgeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ age, graduates }) =>
                        `${age} years (${graduates.toLocaleString()})`
                      }
                      outerRadius={110}
                      fill="#8884d8"
                      dataKey="graduates"
                    >
                      {cleanedAgeData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={`hsl(${index * 60}, 70%, 50%)`}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        `${value.toLocaleString()} graduates`,
                        "Count",
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 5 }}
              >
                No age distribution data available
              </Typography>
            )}
          </CardContent>

          {/* 📊 Summary */}
          {cleanedAgeData.length > 0 && (
            <Box
              sx={{
                p: 2,
                bgcolor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Age Group Summary:
              </Typography>

              {cleanedAgeData.map((entry, index) => {
                const total = cleanedAgeData.reduce(
                  (sum, item) => sum + item.graduates,
                  0
                );
                const percentage = ((entry.graduates / total) * 100).toFixed(1);
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: `hsl(${index * 60}, 70%, 50%)`,
                          borderRadius: "50%",
                          mr: 1,
                        }}
                      />
                      <Typography variant="caption">{entry.age}</Typography>
                    </Box>
                    <Typography variant="caption">
                      <strong>{entry.graduates.toLocaleString()}</strong> (
                      {percentage}%)
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Typography variant="caption" sx={{ color: "#e91e63" }}>
                        ♀ {entry.femalePercent}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#2196f3" }}>
                        ♂ {entry.malePercent}%
                      </Typography>
                    </Box>
                  </Box>
                );
              })}

              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {(() => {
                  const highestGroup = cleanedAgeData.reduce(
                    (max, current) =>
                      current.graduates > max.graduates ? current : max
                  );
                  const firstGroup = cleanedAgeData[0];
                  const lastGroup =
                    cleanedAgeData[cleanedAgeData.length - 1];
                  const totalEmployees = cleanedAgeData.reduce(
                    (sum, g) => sum + g.graduates,
                    0
                  );
                  const youngPercentage = (
                    (firstGroup.graduates / totalEmployees) * 100
                  ).toFixed(1);
                  const seniorPercentage = (
                    (lastGroup.graduates / totalEmployees) * 100
                  ).toFixed(1);
                  const highestPercentage = (
                    (highestGroup.graduates / totalEmployees) * 100
                  ).toFixed(1);

                  return `Young graduates (${firstGroup.age}) represent ${youngPercentage}% (${firstGroup.graduates.toLocaleString()}), while senior group (${lastGroup.age}) represents ${seniorPercentage}% (${lastGroup.graduates.toLocaleString()}). Peak concentration is in the ${highestGroup.age} age group (${highestPercentage}%, ${highestGroup.graduates.toLocaleString()} total).`;
                })()}
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>

      {/* 🟦 Entry & Exit by Field of Study */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 600, display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Entry & Exit by Field of Study (2025)
            </Typography>

            {cleanedEntryExitData.length > 0 ? (
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={cleanedEntryExitData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="fieldOfStudy"
                      angle={-40}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      fontSize={11}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Line
                      type="monotone"
                      dataKey="entries"
                      name="Entry"
                      stroke="#2196f3"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="exits"
                      name="Exit"
                      stroke="#e91e63"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 5 }}
              >
                No entry/exit data available
              </Typography>
            )}
          </CardContent>

          {/* 📊 Field Summary */}
          {cleanedEntryExitData.length > 0 && (
            <Box sx={{ p: 2, bgcolor: "#f5f5f5", borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="subtitle2" gutterBottom>
                Field of Study Trends for 2025
              </Typography>
              <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: "#2196f3", borderRadius: "50%" }} />
                  <Typography variant="body2">Entry</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: "#e91e63", borderRadius: "50%" }} />
                  <Typography variant="body2">Exit</Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {(() => {
                  const sortedEntries = [...cleanedEntryExitData].sort(
                    (a, b) => b.entries - a.entries
                  );
                  const sortedExits = [...cleanedEntryExitData].sort(
                    (a, b) => b.exits - a.exits
                  );

                  const topEntries = sortedEntries.slice(0, 3);
                  const topExit = sortedExits[0];
                  const entryLabel = (item) =>
                    `${item.fieldOfStudy} (${item.entries.toLocaleString()})`;

                  let entryLine = "";
                  if (topEntries.length === 1) {
                    entryLine = `${entryLabel(topEntries[0])} leads entries.`;
                  } else if (topEntries.length === 2) {
                    entryLine = `${entryLabel(topEntries[0])} leads entries, followed by ${entryLabel(
                      topEntries[1]
                    )}.`;
                  } else if (topEntries.length >= 3) {
                    entryLine = `${entryLabel(topEntries[0])} leads entries, followed by ${entryLabel(
                      topEntries[1]
                    )} and ${entryLabel(topEntries[2])}.`;
                  }

                  const exitLine = topExit
                    ? `Exits peak at ${topExit.fieldOfStudy} (${topExit.exits.toLocaleString()}).`
                    : "No exit data available.";

                  return `${entryLine} ${exitLine}`.trim();
                })()}
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>

      {/* 🟪 Gender Balance by Field */}
      <Grid item xs={12}>
        <Card sx={{ height: 700, display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ flex: 1, overflow: "hidden" }}>
            <Typography variant="h6" gutterBottom>
              Gender Balance by Field
            </Typography>

            {cleanedGenderData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={cleanedGenderData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="studyField"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={11}
                    interval={0}
                  />
                  <YAxis label={{ value: "Percentage (%)", angle: -90, position: "insideLeft" }} />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="femalePercent" fill="#e91e63" name="Female %" barSize={40}>
                    <LabelList dataKey="femalePercent" position="top" formatter={(v) => `${v}%`} />
                  </Bar>
                  <Bar dataKey="malePercent" fill="#2196f3" name="Male %" barSize={40}>
                    <LabelList dataKey="malePercent" position="top" formatter={(v) => `${v}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                No gender distribution data available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DemographicsCharts;
